import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client'
import type { AppRouter } from '@via360/api/src/routers/index.js'
import { useCallback, useEffect, useState } from 'react'

/**
 * Cliente tRPC tipado de ponta a ponta: renomear um campo na API quebra a
 * compilacao do console, que e exatamente o que se quer num painel que aperta
 * botao de desligar servidor.
 */
export const api = createTRPCClient<AppRouter>({
  links: [httpBatchLink({ url: '/trpc', fetch: (u, o) => fetch(u, { ...o, credentials: 'include' }) })],
})

export function errorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) return err.message
  if (err instanceof Error) return err.message
  return 'Erro inesperado'
}

/** Hook minimo de consulta com refetch e polling opcional. */
export function useQuery<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
  opts: { pollMs?: number } = {},
) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const run = useCallback(async () => {
    try {
      setData(await fn())
      setError(null)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    void run()
    if (!opts.pollMs) return
    const t = setInterval(() => void run(), opts.pollMs)
    return () => clearInterval(t)
  }, [run, opts.pollMs])

  return { data, error, loading, refetch: run }
}
